import { useEffect, useState } from 'react';

import { getCountry } from '@iwIntlProvider/CountryProvider';
import { tracking } from '@iwoca/frontend-tracking-library';
import { IconFileVisualizationFilled } from '@iwoca/icons';
import { Button } from '@iwoca/orion';
import { REQUEST_CHANGE_ROUTER } from '@iwRouter/IwRouter.actions';
import { defineMessage, FormattedMessage, useIntl } from 'react-intl';
import { connect } from 'react-redux';
import { Dispatch } from 'redux';

import styles from './DeferredView.module.css';
import { AMPLITUDE_CATEGORY } from '../../../tracking/amplitudeCategories';
import { AMPLITUDE_EVENT_NAMES } from '../../../tracking/amplitudeEvents';
import { MainSectionWrapper } from '../../pageStructure/mainSectionWrapper';

const callPhoneNumberMessage = defineMessage({
    id: '_.deferred_view.callPhoneNumber',
    defaultMessage: '+442033973375'
});

const mapDispatchToProps = (dispatch: Dispatch) => ({
    redirectToReapplyForm: () =>
        dispatch({
            type: REQUEST_CHANGE_ROUTER,
            location: {
                pathname: '/apply-flexi-loan/'
            }
        })
});

type InjectedProps = ReturnType<typeof mapDispatchToProps>;

const DeferredView = ({ redirectToReapplyForm }: InjectedProps) => {
    const intl = useIntl();
    const [submittingNewApplication, setSubmittingNewApplication] =
        useState(false);

    useEffect(() => {
        tracking.ampli.track({
            event_type: AMPLITUDE_EVENT_NAMES.APPLICATION_ON_HOLD_VIEWED,
            event_properties: {
                category:
                    getCountry() === 'DE'
                        ? AMPLITUDE_CATEGORY.ACCOUNT_PAGES_DE
                        : AMPLITUDE_CATEGORY.ACCOUNT_PAGES
            }
        });
    }, []);

    function reApplyForLoan() {
        setSubmittingNewApplication(true);
        tracking.ampli.track({
            event_type: AMPLITUDE_EVENT_NAMES.APPLICATION_ON_HOLD_CTA_CLICKED,
            event_properties: {
                category:
                    getCountry() === 'DE'
                        ? AMPLITUDE_CATEGORY.ACCOUNT_PAGES_DE
                        : AMPLITUDE_CATEGORY.ACCOUNT_PAGES
            }
        });

        redirectToReapplyForm();
    }

    return (
        <MainSectionWrapper>
            <div className={styles.innerWrapper}>
                <div className={styles.container}>
                    <div className={styles.fileSearchIconContainer}>
                        <IconFileVisualizationFilled
                            className={styles.fileSearchIcon}
                        />
                    </div>
                    <div className={styles.title}>
                        <FormattedMessage
                            id="_.deferred_view.title"
                            defaultMessage="Your application is on hold"
                        />
                    </div>
                    <div className={styles.subtitle} data-testid="subtitle">
                        <FormattedMessage
                            id="_.deferred_view.subtitle"
                            defaultMessage="If you'd like to re-apply for this application, click on the button beneath. We may require you to submit additional documents."
                        />
                    </div>
                    <Button
                        size="large"
                        variant="primary"
                        className={styles.reApplyCTA}
                        data-testid="reapply-cta"
                        loading={submittingNewApplication}
                        onClick={() => reApplyForLoan()}
                    >
                        <FormattedMessage
                            id="_.deferred_view.reApplyButtonText"
                            defaultMessage="Re-apply"
                        />
                    </Button>
                    <div
                        className={styles.underButtonText}
                        data-testid="underButtonText"
                    >
                        <FormattedMessage
                            id="_.deferred_view.underButtonText"
                            defaultMessage="Or, give us a call on {link} if you have any questions."
                            values={{
                                link: (
                                    <a
                                        className={styles.phoneLink}
                                        href={`tel:${intl.formatMessage(
                                            callPhoneNumberMessage
                                        )}`}
                                    >
                                        <FormattedMessage
                                            id="_.deferred_view.displayPhoneNumber"
                                            defaultMessage="020 3397 3375"
                                        />
                                    </a>
                                )
                            }}
                        />
                    </div>
                </div>
            </div>
        </MainSectionWrapper>
    );
};

// eslint-disable-next-line import/no-default-export
export default connect(null, mapDispatchToProps)(DeferredView);
